import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LanguageToggle from "@/components/language-toggle";
import TierBadge from "@/components/tier-badge";
import { Sparkle, House, UsersThree, Gift, GraduationCap, SignOut } from "@phosphor-icons/react";

export default function AmbassadorLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();

  const items = [
    { to: "/app", label: t.nav.dashboard, icon: House, end: true, testid: "nav-dashboard" },
    { to: "/app/friends", label: t.nav.friends, icon: UsersThree, testid: "nav-friends" },
    { to: "/app/rewards", label: t.nav.rewards, icon: Gift, testid: "nav-rewards" },
    { to: "/app/academy", label: t.nav.academy, icon: GraduationCap, testid: "nav-academy" },
  ];

  return (
    <div className="min-h-screen flex bg-[#f6f2ea]">
      <aside className="hidden md:flex w-64 flex-col border-r border-[#e6dfd2] bg-white">
        <div className="px-6 py-6 border-b border-[#e6dfd2]">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#0d3b66] flex items-center justify-center">
              <Sparkle size={16} weight="fill" color="#f4a261" />
            </div>
            <span className="font-serif-display text-xl">{t.brand}</span>
          </Link>
          <div className="text-[10px] tracking-widest uppercase text-[#6b7280] mt-2">{t.programme}</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-full text-sm ${
                  isActive ? "bg-[#0d3b66] text-[#f4a261]" : "text-[#0b1521] hover:bg-[#0d3b66]/5"
                }`
              }
            >
              <it.icon size={18} weight="duotone" />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#e6dfd2]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#e07a5f] text-white flex items-center justify-center font-medium">
              {user?.name?.[0]}{user?.surname?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name} {user?.surname}</div>
              <div className="mt-1"><TierBadge level={user?.level || "Starter"} size="sm" /></div>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="mt-4 w-full inline-flex items-center gap-2 justify-center px-3 py-2 rounded-full border border-[#e6dfd2] text-sm hover:bg-[#0d3b66]/5">
            <SignOut size={14} /> {t.nav.logout}
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="border-b border-[#e6dfd2] bg-white/70 backdrop-blur-xl sticky top-0 z-10">
          <div className="px-6 md:px-10 py-4 flex items-center justify-between">
            <div className="md:hidden font-serif-display text-lg">{t.brand}</div>
            <div className="ml-auto flex items-center gap-3">
              <LanguageToggle />
              <button data-testid="header-logout-btn" onClick={logout} className="md:hidden text-sm text-[#0d3b66]">{t.nav.logout}</button>
            </div>
          </div>
          {/* Mobile nav */}
          <div className="md:hidden flex overflow-x-auto gap-2 px-4 pb-3">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-full text-xs ${isActive ? "bg-[#0d3b66] text-[#f4a261]" : "bg-white border border-[#e6dfd2]"}`}>
                {it.label}
              </NavLink>
            ))}
          </div>
        </header>
        <main className="px-6 md:px-10 py-8 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
